import {
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Button,
  SvgIcon,
} from "@mui/material";
import { DateRangeEnum } from "common/enums";
import React from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { summarizeConversation } from "requests/tanstack/useConversation";
import showSnackbar from "utils/showSnackbar";
import { useConversationSummaryStore } from "store/useConversationSummaryStore";
import useToggleState from "utils/hooks/useToggleState";
import DateRangeDialog from "./DataRangeDialog";
import { ReactComponent as sumarizeIcon } from "assets/icons/summary.svg";

type Props = {
  conversationId: string;
  deviceId: string;
};

export default function ConversationSumarizerMenu(props: Props) {
  const { setConversationSummary, closeConversationSummary } =
    useConversationSummaryStore();
  const { conversationId, deviceId } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openDateRangeDialog, setOpenDateRangeDialog] = useToggleState();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleCkickConversationSummaryzeOption = async (
    range: DateRangeEnum,
    startDate?: string,
    endDate?: string
  ) => {
    if (range === DateRangeEnum.CUSTOM) {
      if (!startDate || !endDate) {
        showSnackbar("Selecione um intervalo válido!", "warning");
        return;
      }
    }
    handleCloseMenu();
    showSnackbar("Gerando resumo...", "info", 5000);
    try {
      const conversationSumarizeResult = await summarizeConversation(
        conversationId,
        {
          deviceId,
          range,
          startDate,
          endDate,
        }
      );
      setConversationSummary(conversationSumarizeResult, conversationId);
    } catch (error) {
      showSnackbar(`Falha ao gerar resumo! ERR: ${error?.message}`, "error");
      closeConversationSummary(conversationId);
    }
  };

  return (
    <>
      <Button
        aria-label="Alterar prioridade da conversa"
        id="change-priority-button"
        aria-controls={open ? "change-conversation-priority-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="menu"
        onClick={handleClick}
        color="primary"
        sx={{ gap: 1 }}
      >
        <Typography variant="caption" fontWeight="bold">
          Sumarizador
        </Typography>
        <SvgIcon component={sumarizeIcon} />
      </Button>
      <Menu
        id="change-conversation-priority-menu"
        MenuListProps={{
          "aria-labelledby": "change-priority-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            style: {
              border: "1px solid #CCCCD6",
              borderRadius: "11px",
              padding: "8px 0",
              width: "20ch",
            },
          },
        }}
      >
        <MenuItem
          key={DateRangeEnum.LAST_2_HOURS}
          onClick={() =>
            handleCkickConversationSummaryzeOption(DateRangeEnum.LAST_2_HOURS)
          }
        >
          <Typography variant="body2">Últimas 2 horas</Typography>
        </MenuItem>
        <MenuItem
          key={DateRangeEnum.LAST_24_HOURS}
          onClick={() =>
            handleCkickConversationSummaryzeOption(DateRangeEnum.LAST_24_HOURS)
          }
        >
          <Typography variant="body2">Últimas 24 horas</Typography>
        </MenuItem>
        <MenuItem
          key={DateRangeEnum.LAST_7_DAYS}
          onClick={() =>
            handleCkickConversationSummaryzeOption(DateRangeEnum.LAST_7_DAYS)
          }
        >
          <Typography variant="body2">Últimos 7 dias</Typography>
        </MenuItem>
        <MenuItem
          key={DateRangeEnum.LAST_30_DAYS}
          onClick={() =>
            handleCkickConversationSummaryzeOption(DateRangeEnum.LAST_30_DAYS)
          }
        >
          <Typography variant="body2">Últimos 30 dias</Typography>
        </MenuItem>
        <MenuItem
          sx={{ gap: 1 }}
          key={DateRangeEnum.CUSTOM}
          onClick={setOpenDateRangeDialog}
        >
          <Typography variant="body2">Intervalo personalizado</Typography>
          <ListItemIcon>
            <CalendarMonthIcon fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      </Menu>
      <DateRangeDialog
        open={openDateRangeDialog}
        onClose={setOpenDateRangeDialog}
        onApply={(startDate, endDate) => {
          handleCkickConversationSummaryzeOption(
            DateRangeEnum.CUSTOM,
            startDate,
            endDate
          );
        }}
      />
    </>
  );
}
